import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Descriptions, Tag, Table, Collapse } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import StorageUnitContext from "../../../../context/wms/storage-unit/context";
import StorageUnitContextProvider from "../../../../context/wms/storage-unit/provider";
import { StorageUnitType, PlacementSide } from "../../../../models/wms/enums";
import Spin from "../../../general/antd/spin";
import PageDetails from "../../../general/details";

const Details = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { details, actions, loading } = useContext(StorageUnitContext);

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  if (loading.includes("details")) {
    return <Spin />;
  }

  const typeColorMap: Record<number, string> = {
    [StorageUnitType.CABINET]: "green",
    [StorageUnitType.FRIDGE]: "blue",
    [StorageUnitType.TABLE]: "purple",
  };

  const getPlacementSideLabel = (side?: PlacementSide) => {
    if (side === PlacementSide.L) return t("left");
    if (side === PlacementSide.R) return t("right");
    return "-";
  };

  return (
    <PageDetails
      title={t("details")}
      subtitle={t("storage_unit")}
      buttonName={`${t("update_storage_unit")}`}
      onClick={() => window.location.assign(`/wms/storage-units/update/${id}`)}
      loading={loading.includes("details")}
      onRefresh={() => actions.getDetails(Number(id))}
    >
      <Descriptions
        bordered
        column={1}
        size="middle"
      >
        <Descriptions.Item label={t("id")}>{details?.id}</Descriptions.Item>
        {details?.translations?.name?.map((translation) => (
          <Descriptions.Item
            key={translation.lang}
            label={`${t("name")} (${translation.lang})`}
          >
            {translation.value || "-"}
          </Descriptions.Item>
        ))}
        <Descriptions.Item label={t("store")}>
          {details?.store?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("store_id")}>
          {details?.storeId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("zone")}>
          {details?.zone?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("zone_id")}>
          {details?.zoneId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("aisle")}>
          {details?.aisle?.name ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("aisle_id")}>
          {details?.aisleId ?? "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("storage_unit_type")}>
          {details?.storageUnitType !== undefined && (
            <Tag color={typeColorMap[details.storageUnitType] || "default"}>
              {t(`storage_unit_type_${StorageUnitType[details.storageUnitType]?.toLowerCase()}`)}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("placement_side")}>
          {details?.side !== undefined ? getPlacementSideLabel(details.side) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("scanable_code")}>
          {details?.scanableCode || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("description")}>
          {details?.description || "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("pick_sequence")}>
          {details?.pickSequence}
        </Descriptions.Item>
        <Descriptions.Item label={t("is_active")}>
          {details?.isActive ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              {t("active")}
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="default">
              {t("inactive")}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t("created_at")}>
          {details?.createdAt ? String(details.createdAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("created_by")}>
          {details?.createdBy
            ? `${details.createdBy.firstName} ${details.createdBy.lastName} (${details.createdBy.mobile})`
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("updated_at")}>
          {details?.updatedAt ? String(details.updatedAt) : "-"}
        </Descriptions.Item>
        <Descriptions.Item label={t("updated_by")}>
          {details?.updatedBy
            ? `${details.updatedBy.firstName} ${details.updatedBy.lastName} (${details.updatedBy.mobile})`
            : "-"}
        </Descriptions.Item>
      </Descriptions>

      {details?.shelves && details.shelves.length > 0 && (
        <Collapse style={{ marginTop: 16 }} items={[
          {
            key: "shelves",
            label: `${t("shelves")} (${details.shelves.length})`,
            children: (
              <Table
                dataSource={details.shelves}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: t("id"),
                    dataIndex: "id",
                    key: "id",
                    width: 80,
                  },
                  {
                    title: t("name"),
                    dataIndex: "name",
                    key: "name",
                  },
                  {
                    title: t("scanable_code"),
                    dataIndex: "scanableCode",
                    key: "scanableCode",
                  },
                  {
                    title: t("has_bins"),
                    dataIndex: "hasBins",
                    key: "hasBins",
                    width: 100,
                    render: (hasBins: boolean) => (
                      <Tag icon={hasBins ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={hasBins ? "success" : "default"}>
                        {hasBins ? t("yes") : t("no")}
                      </Tag>
                    ),
                  },
                  {
                    title: t("is_active"),
                    dataIndex: "isActive",
                    key: "isActive",
                    width: 100,
                    render: (isActive: boolean) => (
                      <Tag icon={isActive ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={isActive ? "success" : "default"}>
                        {isActive ? t("active") : t("inactive")}
                      </Tag>
                    ),
                  },
                ]}
              />
            ),
          },
        ]} />
      )}
    </PageDetails>
  );
};

const StorageUnitDetails = () => {
  return (
    <StorageUnitContextProvider>
      <Details />
    </StorageUnitContextProvider>
  );
};

export default StorageUnitDetails;
